import React, { useEffect, useMemo, useState } from "react";
import { RecIcon } from "../materials/MaterialRecordParts";
import {
  PLAN_KIND,
  dayIndex,
  dayIndexToISO,
} from "../../helpers/planning/planningModel";
import {
  addMonths,
  dayInMonth,
  eventsForDay,
  indexEventsByDay,
  monthGrid,
  monthStartIndex,
} from "../../helpers/planning/calendarModel";
import { TaskStatePill } from "../tasks/TaskParts";
import { formatDuration } from "../../helpers/tasks/taskDuration";
import { WORK_SPAN } from "../../helpers/planning/planningModel";
import "../tasks/tasks.css";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const utc = (index, options) =>
  new Date(index * 86400000).toLocaleDateString("en-US", { ...options, timeZone: "UTC" });

/** Same marker shapes as the Timeline, so the two views read as one system. */
function TypeShape({ kind, overdue }) {
  return <i className={`pc-shape pc-shape--${kind}${overdue ? " is-overdue" : ""}`} aria-hidden="true" />;
}

const shortDate = (iso, todayYear) => {
  const idx = dayIndex(iso);
  if (idx == null) return "";
  const withYear = String(iso).slice(0, 4) !== todayYear;
  return utc(idx, withYear ? { month: "short", day: "numeric", year: "numeric" } : { month: "short", day: "numeric" });
};

/**
 * Scheduling facts for one task card, from stored / derived values only.
 * The card sits on its Due date under "Task due", so Due is not repeated.
 * Missing values are simply absent; Duration is never inferred from dates.
 */
export function taskScheduleFacts(event, { minutesPerDay = null, todayYear = "" } = {}) {
  const facts = [];
  if (event.startDate) facts.push({ key: "start", label: "Start", value: shortDate(event.startDate, todayYear) });
  const duration = formatDuration(event.durationMinutes, minutesPerDay);
  if (duration) facts.push({ key: "duration", label: "Planned", value: duration });
  if (event.spanStatus === WORK_SPAN.OK && event.expectedDate) {
    facts.push({ key: "expected", label: "Expected", value: shortDate(event.expectedDate, todayYear), warn: event.expectedAfterDue ? "after due" : "" });
  } else if (event.spanStatus === WORK_SPAN.SCHEDULE_UNAVAILABLE) {
    facts.push({ key: "expected", label: "Expected", value: "unavailable (work schedule not loaded)", muted: true });
  } else if (event.spanStatus === WORK_SPAN.UNAVAILABLE) {
    facts.push({ key: "expected", label: "Expected", value: "unavailable", muted: true });
  }
  return facts;
}

/**
 * Planning Calendar V1: a month projection of the Planning events plus the
 * selected day's agenda. Read-only; the page scrolls normally (nothing here
 * scrolls internally).
 */
export function PlanningCalendar({ events, today, onOpenEvent, initialSelected, initialMonth, onSelectionChange, minutesPerDay = null }) {
  const todayIdx = dayIndex(today);
  const todayYear = String(today || "").slice(0, 4);
  // Optional restore (returning from an item opened out of Planning); defaults to today.
  const [monthStart, setMonthStart] = useState(() => initialMonth ?? monthStartIndex(todayIdx));
  const [selected, setSelected] = useState(() => initialSelected ?? todayIdx);

  useEffect(() => {
    onSelectionChange?.({ selected, monthStart });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, monthStart]);

  const byDay = useMemo(() => indexEventsByDay(events), [events]);
  const weeks = useMemo(() => monthGrid(monthStart), [monthStart]);
  const agenda = eventsForDay(byDay, selected);

  function goToMonth(next) {
    setMonthStart(next);
    // Keep an agenda that matches the visible month: today when it is shown,
    // otherwise the 1st.
    setSelected(monthStartIndex(todayIdx) === next ? todayIdx : next);
  }

  function goToday() {
    setMonthStart(monthStartIndex(todayIdx));
    setSelected(todayIdx);
  }

  function pick(index) {
    setSelected(index);
    // A spillover day moves the calendar to its own month (least surprising).
    if (!dayInMonth(index, monthStart)) setMonthStart(monthStartIndex(index));
  }

  return (
    <div className="pc-wrap" data-testid="planning-calendar">
      <div className="pl-toolbar pc-head">
        <button type="button" className="pl-iconbtn" aria-label="Previous month" onClick={() => goToMonth(addMonths(monthStart, -1))}>
          <RecIcon name="back" />
        </button>
        <div className="pl-range" data-testid="calendar-month">
          {utc(monthStart, { month: "long", year: "numeric" })}
        </div>
        <button type="button" className="pl-iconbtn" aria-label="Next month" onClick={() => goToMonth(addMonths(monthStart, 1))}>
          <RecIcon name="chevron" />
        </button>
        <button type="button" className="pl-today-btn" onClick={goToday}>
          Today
        </button>
      </div>

      <div className="pc-card">
        <div className="pc-weekdays" aria-hidden="true">
          {WEEKDAYS.map((d) => (
            <span key={d}>{d}</span>
          ))}
        </div>
        <div className="pc-grid" role="grid" aria-label={utc(monthStart, { month: "long", year: "numeric" })}>
          {weeks.flat().map((idx) => {
            const dayEvents = eventsForDay(byDay, idx);
            const count = dayEvents.length;
            const inMonth = dayInMonth(idx, monthStart);
            const overdue = dayEvents.some((e) => e.overdue);
            const iso = dayIndexToISO(idx);
            return (
              <button
                type="button"
                key={idx}
                role="gridcell"
                data-date={iso}
                data-count={count}
                aria-selected={idx === selected}
                aria-current={idx === todayIdx ? "date" : undefined}
                aria-label={`${utc(idx, { weekday: "long", month: "long", day: "numeric" })}${
                  count ? `, ${count} item${count === 1 ? "" : "s"}` : ""
                }`}
                className={`pc-cell${inMonth ? "" : " is-outside"}${idx === selected ? " is-selected" : ""}${
                  idx === todayIdx ? " is-today" : ""
                }`}
                onClick={() => pick(idx)}
              >
                <span className="pc-num">{Number(iso.slice(8, 10))}</span>
                {count > 0 ? (
                  <span className={`pc-count${overdue ? " is-overdue" : ""}`}>{count}</span>
                ) : (
                  <span className="pc-count pc-count--empty" aria-hidden="true" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <section className="pc-agenda" aria-label="Selected day agenda" data-testid="calendar-agenda">
        <div className="pc-agenda__head">
          <strong>{utc(selected, { weekday: "long", month: "long", day: "numeric" })}</strong>
          <span>{agenda.length ? `${agenda.length} item${agenda.length === 1 ? "" : "s"}` : ""}</span>
        </div>
        {agenda.length === 0 ? (
          <div className="pc-empty">No scheduled items.</div>
        ) : (
          <ul className="pc-list">
            {agenda.map((event) => {
              const body = (
                <>
                  <span className="pc-item__type">
                    <TypeShape kind={event.kind} overdue={event.overdue} />
                    {event.label}
                    {event.overdue ? <em>Overdue</em> : null}
                  </span>
                  <span className="pc-item__title">{event.title}</span>
                  {event.kind === PLAN_KIND.TASK ? (
                    <span className="pc-item__state">
                      <TaskStatePill
                        task={{ status: event.completed ? "completed" : "open", progress_percent: event.progress }}
                      />
                    </span>
                  ) : null}
                  {event.kind === PLAN_KIND.TASK
                    ? (() => {
                        const facts = taskScheduleFacts(event, { minutesPerDay, todayYear });
                        return facts.length ? (
                          <span className="pc-item__sched" data-testid="calendar-task-schedule">
                            {facts.map((f) => (
                              <span key={f.key} className={`pc-fact${f.muted ? " is-muted" : ""}`} data-fact={f.key}>
                                <b>{f.label}</b> {f.value}
                                {f.warn ? <em className="pc-fact__warn">{f.warn}</em> : null}
                              </span>
                            ))}
                          </span>
                        ) : null;
                      })()
                    : null}
                  {event.kind === PLAN_KIND.PROJECT ? (
                    <span className="pc-item__ctx">Target completion</span>
                  ) : (
                    <span className="pc-item__ctx">{event.context}</span>
                  )}
                </>
              );
              return (
                <li key={event.key} className={`pc-item pc-item--${event.kind}${event.overdue ? " is-overdue" : ""}${event.completed ? " is-done" : ""}${onOpenEvent ? " is-openable" : ""}`} data-kind={event.kind}>
                  {onOpenEvent ? (
                    <button
                      type="button"
                      className="pc-item__btn"
                      aria-label={`Open ${event.label.toLowerCase()}: ${event.title}`}
                      onClick={() =>
                        onOpenEvent({
                          kind: event.kind,
                          id: event.id,
                          projectId: event.projectId,
                          locationId: event.locationId,
                          featureId: event.featureId,
                        })
                      }
                    >
                      {body}
                    </button>
                  ) : (
                    body
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
